/**
 * Demo-account containment for candidate listings.
 *
 * Production carries a few permanent demo accounts (a demo employer and demo candidates used in
 * live sales demos). Demo candidates are real `publicProfiles` docs, so without containment every
 * real employer would see them in candidate search and Top Matches — people who do not exist,
 * indistinguishable from real hires. The rule runs both ways: a demo candidate is listed only for a
 * viewer that is a demo account itself, and a demo viewer is shown ONLY demo candidates — the demo
 * employer is used on sales screen-shares, where a real EMT's name must never appear.
 *
 * `isDemoAccount` is server-owned: it is set on profiles/{uid} by an admin script only
 * (firestore.rules rejects client writes of it) and mirrored onto publicProfiles/{uid} by
 * functions/src/readinessSync.ts. Direct reads by id (an applicant's profile, /p/{uid}) are
 * deliberately unaffected — only listings are filtered.
 */

let viewerIsDemo = false;
// A practice EMT login (scripts/demo-trident-practice) is tied to ONE practice company: it lists that
// company's jobs and no other demo company's, listed or by direct link. Narrows; grants nothing.
let viewerPracticeOrgId: string | null = null;

/** Called by AuthContext whenever the signed-in profile changes. */
export function setDemoViewer(profile: { isDemoAccount?: unknown; isPracticeAccount?: unknown; practiceOrgId?: unknown } | null | undefined): void {
  viewerIsDemo = profile?.isDemoAccount === true;
  viewerPracticeOrgId = viewerIsDemo && profile?.isPracticeAccount === true && typeof profile?.practiceOrgId === 'string' && profile.practiceOrgId ? profile.practiceOrgId : null;
}

export function isDemoViewer(): boolean {
  return viewerIsDemo;
}

export function isDemoProfile(profile: object | null | undefined): boolean {
  return (profile as { isDemoAccount?: unknown } | null | undefined)?.isDemoAccount === true;
}

/**
 * Job side of the same rule: a job posted by a demo employer carries a server-stamped `isDemoJob`
 * (functions/src/emergencyJobs.ts) and is listed only for demo viewers. Opening it by direct link
 * is unaffected, so an existing demo application keeps working.
 *
 * A job from a showcase-hidden TEST account (server-stamped `demoShowcaseHidden`, see
 * isDemoShowcaseHidden below) is listed for nobody — not even demo viewers.
 */
export function isHiddenDemoJob(job: object | null | undefined): boolean {
  if (isDemoShowcaseHidden(job)) return true;
  if (viewerPracticeOrgId && (job as { orgId?: unknown } | null | undefined)?.orgId !== viewerPracticeOrgId) return true;
  return !viewerIsDemo && (job as { isDemoJob?: unknown } | null | undefined)?.isDemoJob === true;
}

/**
 * A practice EMT opening a job that is not its practice company's, by a direct link. The practice
 * login treats it as a job that does not exist; the server refuses the application as well
 * (applyToJob). Everyone else is unaffected, so this narrows the demo viewer only.
 */
export function isOffLimitsForPracticeViewer(job: object | null | undefined): boolean {
  return viewerPracticeOrgId !== null && (job as { orgId?: unknown } | null | undefined)?.orgId !== viewerPracticeOrgId;
}

/**
 * Organizations of demo employers (company discovery map) and demo institutes (renewal panel,
 * "train nearby" pointers), same rule — and showcase-hidden ones are listed for nobody.
 */
export function isHiddenDemoOrg(org: object | null | undefined): boolean {
  if (isDemoShowcaseHidden(org)) return true;
  return !viewerIsDemo && isDemoProfile(org);
}

/**
 * Demo-flagged TEST accounts (own test logins, a prospect's trial account) are contained like any
 * demo account, but unlike the showcase fixtures they must not be on screen in a live demo — the
 * demo employer sees every demo account, and a demo candidate sees every demo employer's jobs and
 * every demo institute. An admin script marks the listed docs `demoShowcaseHidden: true`:
 *   - candidates: publicProfiles/{uid} (scripts/tmp-demo-hide-test-accounts.mjs) — the employer
 *     dashboard's Top Matches tile skips them;
 *   - employers and institutes: profiles/{uid} plus their organizations/{uid}, institutes/{uid}
 *     and jobs (scripts/tmp-demo-hide-test-accounts-candidate-views.mjs) — the job board, the
 *     company map and the renewal / "train nearby" institute lists skip them for every viewer.
 *     New jobs from such an employer are stamped by the server (functions/src/emergencyJobs.ts).
 * Every writer of those docs merges, so the flag survives profile syncs; an account setting it on
 * its own doc could only hide itself.
 */
export function isDemoShowcaseHidden(profile: object | null | undefined): boolean {
  return (profile as { demoShowcaseHidden?: unknown } | null | undefined)?.demoShowcaseHidden === true;
}

/**
 * Candidate listings under demo containment: a real viewer gets only real candidates, a demo viewer
 * only demo candidates. (Was withoutDemoProfiles, which gave a demo viewer everyone.)
 *
 * Showcase-hidden test accounts are listed for nobody (2026-10-04). Only the dashboard's Top
 * Matches tile used to drop them, so Candidate Search and the checklist matches showed "Test Test",
 * "TEST Candidate" and a prospect's own test login to the demo employer on screen-shares.
 */
export function candidatesForViewer<T extends object>(profiles: T[]): T[] {
  return profiles.filter((p) => isDemoProfile(p) === viewerIsDemo && !isDemoShowcaseHidden(p));
}
